import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "../../shared utils/dayjsConfig.js";
import { mondayOfWeek } from "../../Timetable/utils/timetableUtils.js";
import { getWeeklyStaffScheduling } from "../../Timetable/api/staffSchedulingApi.js";
import {
  getActivityGroupsByDate,
  getActivityLeaders,
} from "../../Activities/api/activitiesApi.js";
import { fetchClassGroups } from "../../Classes/api/classesApi.js";
import { getDailyRota, saveDailyRota } from "../api/dailyRotaApi.js";
import {
  buildSeedBlocks,
  mergeSaved,
  toSaved,
  buildStaffPool,
  buildKeyStaff,
  collectStaffIds,
  buildHoursBaseline,
  computeStaffMinutes,
  buildStaffIndex,
} from "../utils/rotaModel.js";

// Loads everything a day's rota needs for one campus + date:
//   • saved rota (if any) — else a seed from the day template + activity groups
//   • the on-duty staff pool (weekly staff-scheduling feed, filtered to the date)
//   • the camp-manager / DSL / first-aid key-staff strip
//   • the backend's base assigned-hours calculation (`scheduledMinutes` on the
//     activity-leaders feed) plus the local figure for the day AS LOADED, which
//     together let the view track hours as a delta on the backend number
// Exposes `save(blocks)` to persist. Each feed degrades independently — a
// missing endpoint yields an empty section rather than a broken page.
export default function useDailyRota(campusName, dateStr) {
  const [state, setState] = useState({
    loading: false,
    error: null,
    seedBlocks: [],
    staffPool: [],
    keyStaff: {},
    wasSaved: false,
    staffIndex: null,
    hoursBaseline: null,
    hoursBaselineAt: null,
  });
  const reqId = useRef(0);

  useEffect(() => {
    if (!campusName || !dateStr) {
      setState({ loading: false, error: null, seedBlocks: [], staffPool: [], keyStaff: {}, wasSaved: false, staffIndex: null, hoursBaseline: null, hoursBaselineAt: null });
      return undefined;
    }
    const myReq = ++reqId.current;
    setState((s) => ({ ...s, loading: true, error: null }));

    const weekStart = mondayOfWeek(dateStr)?.format("YYYY-MM-DD");
    Promise.all([
      getDailyRota(campusName, dateStr).catch(() => null),
      getActivityGroupsByDate(dateStr, campusName).catch(() => []),
      getActivityLeaders(dateStr, campusName).catch(() => []),
      fetchClassGroups(campusName, dateStr, dateStr).catch(() => []),
      weekStart ? getWeeklyStaffScheduling(campusName, weekStart).catch(() => null) : Promise.resolve(null),
    ])
      .then(([saved, groups, leaders, classes, weekly]) => {
        if (myReq !== reqId.current) return; // a newer load superseded this one
        // Resolves a person to their staffId (see rotaModel staffKeyOf) — built
        // once here off the two feeds that carry staffId, adultId and name.
        const index = buildStaffIndex({ leaders, weekly });
        const reflectedInputs = { groups, leaders, classes, dateStr, weekly, index, staffIds: collectStaffIds(weekly, leaders) };
        const seedBlocks = saved ? mergeSaved(saved, reflectedInputs) : buildSeedBlocks(reflectedInputs);
        setState({
          loading: false,
          error: null,
          seedBlocks,
          staffPool: buildStaffPool(weekly, dateStr),
          keyStaff: buildKeyStaff(weekly, dateStr),
          wasSaved: !!saved,
          staffIndex: index,
          // The backend's figure, and what the same day computes to locally right
          // now — the view charges only the difference from here on.
          hoursBaseline: buildHoursBaseline(leaders, index),
          hoursBaselineAt: computeStaffMinutes(seedBlocks, index),
        });
      })
      .catch((err) => {
        if (myReq !== reqId.current) return;
        setState((s) => ({ ...s, loading: false, error: err.message || "Failed to load rota" }));
      });

    return () => {
      reqId.current += 1; // invalidate in-flight results on unmount / dep change
    };
  }, [campusName, dateStr]);

  const save = useCallback(
    (blocks) =>
      saveDailyRota({
        campus: campusName,
        date: dayjs(dateStr).format("YYYY-MM-DD"),
        blocks: toSaved(blocks),
      }),
    [campusName, dateStr]
  );

  return { ...state, save };
}
